import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { config } from '../../config';
import {
  applyAppFondo,
  DEFAULT_FONDO_OPACIDAD,
  fondoOpacidadToAlpha
} from '../../utils/appFondo';

const FondoPantallaConfig = ({ userInfo, setUserInfo, setError, setSuccess }) => {
  const { updateUser } = useAuth();
  const [fondoLoading, setFondoLoading] = useState(false);
  const [opacidad, setOpacidad] = useState(DEFAULT_FONDO_OPACIDAD);
  const [guardandoOpacidad, setGuardandoOpacidad] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setOpacidad(
      userInfo?.fondoOpacidad != null ? userInfo.fondoOpacidad : DEFAULT_FONDO_OPACIDAD
    );
  }, [userInfo?.fondoOpacidad]);

  const getFondoPreviewUrl = () => {
    if (!userInfo?.fondoPersonalizado) return null;
    const v = userInfo.updatedAt ? new Date(userInfo.updatedAt).getTime() : Date.now();
    return `${config.uploadsUrl}/${userInfo.fondoPersonalizado}?v=${v}`;
  };

  const aplicarEnApp = useCallback(
    (nuevaOpacidad) => {
      applyAppFondo({
        fondoPersonalizado: userInfo?.fondoPersonalizado,
        fondoOpacidad: nuevaOpacidad,
        updatedAt: userInfo?.updatedAt
      });
    },
    [userInfo?.fondoPersonalizado, userInfo?.updatedAt]
  );

  const guardarOpacidad = useCallback(
    async (valor) => {
      if (!userInfo?.fondoPersonalizado) return;

      setGuardandoOpacidad(true);
      try {
        const response = await api.patch('/user/me/fondo-opacidad', {
          fondoOpacidad: valor
        });
        setUserInfo(response.data.user);
        updateUser(response.data.user);
      } catch (err) {
        setError(err.response?.data?.error || 'Error al guardar la opacidad');
        aplicarEnApp(
          userInfo.fondoOpacidad != null ? userInfo.fondoOpacidad : DEFAULT_FONDO_OPACIDAD
        );
        setOpacidad(
          userInfo.fondoOpacidad != null ? userInfo.fondoOpacidad : DEFAULT_FONDO_OPACIDAD
        );
      } finally {
        setGuardandoOpacidad(false);
      }
    },
    [userInfo, setUserInfo, updateUser, setError, aplicarEnApp]
  );

  const handleOpacidadChange = (e) => {
    const valor = parseInt(e.target.value, 10);
    setOpacidad(valor);
    aplicarEnApp(valor);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      guardarOpacidad(valor);
    }, 350);
  };

  const handleOpacidadCommit = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    guardarOpacidad(opacidad);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleUploadFondo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    const permitidos = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    if (!permitidos.includes(ext)) {
      setError('Formato no permitido. Use JPG, PNG, GIF o WEBP.');
      e.target.value = '';
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      setError('La imagen no puede superar 15 MB.');
      e.target.value = '';
      return;
    }

    setFondoLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('fondo', file);
      const response = await api.post('/user/me/fondo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUserInfo(response.data.user);
      updateUser(response.data.user);
      const op = response.data.user.fondoOpacidad ?? DEFAULT_FONDO_OPACIDAD;
      setOpacidad(op);
      aplicarEnApp(op);
      setSuccess('Fondo de pantalla actualizado');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al subir el fondo');
    } finally {
      setFondoLoading(false);
      e.target.value = '';
    }
  };

  const handleRemoveFondo = async () => {
    if (!userInfo?.fondoPersonalizado) return;
    if (!window.confirm('¿Quitar el fondo personalizado y volver al predeterminado?')) return;

    setFondoLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.delete('/user/me/fondo');
      setUserInfo(response.data.user);
      updateUser(response.data.user);
      setOpacidad(DEFAULT_FONDO_OPACIDAD);
      applyAppFondo({ fondoPersonalizado: null });
      setSuccess('Fondo restaurado al predeterminado');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al quitar el fondo');
    } finally {
      setFondoLoading(false);
    }
  };

  const previewAlpha = fondoOpacidadToAlpha(opacidad);

  return (
    <div className="row mt-4">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5>Fondo de pantalla</h5>
          </div>
          <div className="card-body">
            <p className="text-muted mb-3">
              Personaliza el fondo de la aplicación. Formatos: JPG, PNG, <strong>GIF</strong> o WEBP (máx. 15 MB).
            </p>

            {getFondoPreviewUrl() && (
              <div
                className="fondo-pantalla-preview mb-3"
                style={{
                  backgroundImage: `url("${getFondoPreviewUrl()}")`,
                  '--preview-overlay-alpha': previewAlpha
                }}
                title="Vista previa del fondo actual"
              >
                <div className="fondo-pantalla-preview__overlay" aria-hidden="true" />
              </div>
            )}

            {userInfo.fondoPersonalizado && (
              <div className="fondo-opacidad-control">
                <label htmlFor="fondo-opacidad-range" className="form-label d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>
                    Velado sobre el fondo
                    <small className="d-block text-muted fw-normal">
                      0% = imagen nítida · 100% = más capa clara encima
                    </small>
                  </span>
                  <span className="fondo-opacidad-valor text-primary">
                    {opacidad}%
                    {guardandoOpacidad && (
                      <small className="text-muted fw-normal ms-1">guardando…</small>
                    )}
                  </span>
                </label>
                <input
                  id="fondo-opacidad-range"
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={opacidad}
                  onChange={handleOpacidadChange}
                  onMouseUp={handleOpacidadCommit}
                  onTouchEnd={handleOpacidadCommit}
                  disabled={fondoLoading}
                />
              </div>
            )}

            <div className="d-flex flex-wrap gap-2 align-items-center mt-3">
              <label className={`btn btn-primary mb-0 ${fondoLoading ? 'disabled' : ''}`}>
                {fondoLoading ? 'Subiendo...' : (userInfo.fondoPersonalizado ? 'Cambiar imagen' : 'Subir imagen')}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={handleUploadFondo}
                  disabled={fondoLoading}
                  style={{ display: 'none' }}
                />
              </label>
              {userInfo.fondoPersonalizado && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleRemoveFondo}
                  disabled={fondoLoading}
                >
                  Quitar fondo
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FondoPantallaConfig;
